import React, { useContext, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowLeftRight, Circle, FilePlus, Inbox, Pencil, Stamp, UserPlus, Wrench, XCircle } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { isRTL } from "../../utils/rtl";
import { toast } from "../../utils/toast";
import ModalCloseButton from "../common/ModalCloseButton";
import { fetchInventoryTimeline } from "../../service/MeterInventoryManagement";
import { SealChips, SealInfoIcon, SealNumbersPanel } from "./SealNumberChips";
import { getUserInfoAPI } from "../../service/User";
import {
  InlineSpinner,
  formatSealRange,
  isSealType,
  itemTypeOptionsFor,
  sealNumberList,
  sealStockSummary,
  statusOptionsFor,
  useInventoryScope,
} from "./inventoryFormShared";
import {
  buildFieldDiff,
  eventTagData,
  eventTitle,
  eventTone,
  formatEventTimestamp,
  latestHistorySnapshot,
  sortEventsByTimestampDesc,
  uniquePerformerIds,
} from "./inventoryHistoryShared";

const EVENT_ICON = {
  RECEIVE: Inbox,
  INSTALL: Wrench,
  REMOVE: XCircle,
  UPDATE: Pencil,
  CREATE: FilePlus,
  ASSIGN: UserPlus,
  TRANSFER: ArrowLeftRight,
};

const TONE_CLASS = {
  blue: "bg-[#E8F1FB] text-[#236DB4]",
  green: "bg-emerald-50 text-emerald-700",
  red: "bg-red-50 text-red-700",
  amber: "bg-amber-50 text-amber-700",
  teal: "bg-teal-50 text-teal-700",
  cyan: "bg-cyan-50 text-cyan-700",
  slate: "bg-slate-100 text-slate-600",
  purple: "bg-purple-50 text-purple-700",
};

const STATUS_TONE_CLASS = {
  AVAILABLE: TONE_CLASS.blue,
  INSTALLED: TONE_CLASS.green,
  REMOVED: TONE_CLASS.red,
  ASSIGNED: TONE_CLASS.teal,
  REASSIGNED: TONE_CLASS.cyan,
  IN_TRANSIT: TONE_CLASS.amber,
  RETURNED: TONE_CLASS.amber,
  DAMAGED: TONE_CLASS.red,
  QUARANTINED: TONE_CLASS.purple,
  LOST: TONE_CLASS.red,
  RETIRED: TONE_CLASS.slate,
};

const VIEW_FILTERS = ["all", "TRANSACTION", "HISTORY"];
// A seal range's timeline also has one SEAL_USED event per fitted seal.
const SEAL_VIEW_FILTERS = [...VIEW_FILTERS, "SEAL_USED"];

const VIEW_FILTER_LABEL_KEYS = {
  all: "inventoryHistoryFilterAll",
  TRANSACTION: "inventoryHistoryFilterTransactions",
  HISTORY: "inventoryHistoryFilterAuditLog",
  SEAL_USED: "inventorySealsFitted",
};

// Readable names for diff fields that are not plain metadata keys.
const DIFF_FIELD_LABEL_KEYS = {
  sealNumbers: "inventorySealNumbers",
  sealPrefix: "inventorySealPrefix",
  sealFrom: "inventorySealFrom",
  sealTo: "inventorySealTo",
};

export const InventoryHistoryDrawer = ({ open, item: openedItem, onClose }) => {
  const { langMode } = useContext(AppContext);
  const isRtl = isRTL(langMode);
  const t = translations[langMode] || translations.en;
  // Super admins are not tied to one organization, so the scoped org/project
  // travels with the request.
  const { organizationId, projectId } = useInventoryScope();

  const [events, setEvents] = useState([]);
  const [meterIdentifier, setMeterIdentifier] = useState("");
  // Item-level fields the timeline returns next to `events`: itemType, status, the
  // seals fitted on a meter (sealNumbers) and a seal item's range.
  const [itemSummary, setItemSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [viewFilter, setViewFilter] = useState("all");
  const [userNames, setUserNames] = useState({});
  // A meter opened from a seal range's "Fitted seals" table; Back returns to the range.
  const [meterFromSeal, setMeterFromSeal] = useState(null);

  useEffect(() => {
    setMeterFromSeal(null);
  }, [open, openedItem?.id]);

  const item = meterFromSeal || openedItem;
  const itemId = item?.id;

  // Reset per-item state and fetch the timeline whenever a new item is opened.
  useEffect(() => {
    setViewFilter("all");
    if (!open || !itemId) {
      if (!open) {
        setEvents([]);
        setItemSummary({});
        setUserNames({});
      }
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(false);
      // Clear the previous item's details so a meter opened from a seal range
      // doesn't briefly show the range's title and counts.
      setItemSummary({});
      setMeterIdentifier(item?.meterNumber || "");
      try {
        const payload = await fetchInventoryTimeline(itemId, { organizationId, projectId });
        if (cancelled) return;
        setEvents(sortEventsByTimestampDesc(payload?.events));
        setMeterIdentifier(payload?.identifier || item?.meterNumber || "");
        setItemSummary({
          itemType: payload?.itemType,
          status: payload?.status,
          sealNumbers: payload?.sealNumbers,
          sealPrefix: payload?.sealPrefix,
          sealFrom: payload?.sealFrom,
          sealTo: payload?.sealTo,
          totalSealCount: payload?.totalSealCount,
          usedSealCount: payload?.usedSealCount,
          availableSealCount: payload?.availableSealCount,
          usedSeals: Array.isArray(payload?.usedSeals) ? payload.usedSeals : [],
        });
      } catch (error) {
        if (cancelled) return;
        setEvents([]);
        setItemSummary({});
        setLoadError(true);
        toast.error(t.inventoryHistoryLoadFailed);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemId]);

  // Resolve performedBy/changedBy user IDs to display names, once per unique ID.
  useEffect(() => {
    if (!open || !events.length) return undefined;
    const idsToResolve = uniquePerformerIds(events).filter((id) => !(id in userNames));
    if (!idsToResolve.length) return undefined;
    let cancelled = false;
    idsToResolve.forEach((id) => {
      getUserInfoAPI(id)
        .then((response) => {
          if (cancelled) return;
          const name = response?.data?.body?.value?.name;
          setUserNames((prev) => ({ ...prev, [id]: name || null }));
        })
        .catch(() => {
          if (!cancelled) setUserNames((prev) => ({ ...prev, [id]: null }));
        });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, events]);

  const currentState = useMemo(() => latestHistorySnapshot(events), [events]);
  const filteredEvents = useMemo(
    () => (viewFilter === "all" ? events : events.filter((event) => event.eventType === viewFilter)),
    [events, viewFilter]
  );

  if (!open) return null;

  const meta = currentState.metadata || {};
  // The timeline's own itemType/status are the item's current values; the latest
  // history snapshot is only a fallback for older responses.
  const currentStatus = itemSummary.status || currentState.status;
  const currentItemType = itemSummary.itemType || currentState.itemType || item?.raw?.itemType;
  const statusLabel =
    statusOptionsFor(t).find((option) => option.value === currentStatus)?.label ||
    (currentStatus === "REASSIGNED" ? t.inventoryStatusReassigned : currentStatus);
  const typeLabel =
    itemTypeOptionsFor(t).find((option) => option.value === currentItemType)?.label || currentItemType;
  const isSeal = isSealType(currentItemType);
  const sealRange = isSeal ? formatSealRange({ ...currentState, ...itemSummary }) : "";
  // Counts come from the timeline (availableSealCount drops as seals are fitted).
  const sealStock = isSeal
    ? sealStockSummary({
        sealFrom: itemSummary.sealFrom ?? currentState.sealFrom,
        sealTo: itemSummary.sealTo ?? currentState.sealTo,
        totalSealCount: itemSummary.totalSealCount,
        usedSealCount: itemSummary.usedSealCount,
        availableSealCount: itemSummary.availableSealCount,
        usedSealNumbers: (itemSummary.usedSeals || []).map((seal) => seal?.sealNumber),
      })
    : null;
  const usedSeals = isSeal ? itemSummary.usedSeals || [] : [];
  // On a meter, the seals fitted on it.
  const fittedSeals = isSeal ? [] : sealNumberList(itemSummary.sealNumbers ?? currentState.sealNumbers);
  const viewFilters = isSeal ? SEAL_VIEW_FILTERS : VIEW_FILTERS;
  const sealCountTiles = sealStock
    ? [
        { key: "total", label: t.inventorySealTotal, value: sealStock.total },
        { key: "used", label: t.inventorySealFittedCount, value: sealStock.used },
        { key: "available", label: t.inventorySealRemaining, value: sealStock.available },
      ].filter((tile) => tile.value !== null && tile.value !== undefined)
    : [];

  return (
    <div className="fixed inset-0 z-[1250]">
      <button
        type="button"
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-label={t.inventoryClosePanel}
      />
      <aside
        className={`absolute top-0 ${
          isRtl ? "left-0" : "right-0"
        } h-[100dvh] w-[92vw] max-w-[560px] sm:w-[560px] sm:max-w-[560px] bg-white shadow-2xl border-l border-slate-200 flex flex-col`}
        dir={isRtl ? "rtl" : "ltr"}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div>
            {meterFromSeal && (
              <button
                type="button"
                onClick={() => setMeterFromSeal(null)}
                className="mb-1.5 inline-flex items-center gap-1 text-xs font-semibold text-[#236DB4] hover:underline"
                data-testid="inventory-history-back-to-seal"
              >
                <ArrowLeft size={13} className={isRtl ? "rotate-180" : ""} aria-hidden="true" />
                {t.inventorySealBackToRange}
              </button>
            )}
            <h2 className="text-lg font-semibold text-slate-800">
              {isSeal ? t.inventorySealRangeHistoryTitle : t.inventoryMeterHistoryTitle}
            </h2>
            <div className="text-sm text-slate-500 mt-1">
              {t.inventoryMeterHistorySubtitle.split("{identifier}")[0]}
              <span className="font-semibold text-[#236DB4]">
                {meterIdentifier || item?.meterNumber || t.notAvailable}
              </span>
              {t.inventoryMeterHistorySubtitle.split("{identifier}")[1]}
            </div>
          </div>
          <ModalCloseButton onClick={onClose} className="shrink-0" ariaLabel={t.inventoryClosePanel} />
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <InlineSpinner size={28} />
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  {typeLabel && (
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${TONE_CLASS.blue}`}>
                      {typeLabel}
                    </span>
                  )}
                  {statusLabel && (
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        STATUS_TONE_CLASS[currentState.status] || TONE_CLASS.slate
                      }`}
                    >
                      {statusLabel}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {sealRange && (
                    <div>
                      <div className="text-slate-400 font-semibold uppercase">{t.inventorySealRange}</div>
                      <div className="text-slate-700 font-medium mt-0.5" data-testid="inventory-history-seal-range">
                        {sealRange}
                      </div>
                    </div>
                  )}
                  {sealCountTiles.map((tile) => (
                    <div key={tile.key}>
                      <div className="text-slate-400 font-semibold uppercase">{tile.label}</div>
                      <div
                        className={`font-medium mt-0.5 ${tile.key === "available" ? "text-emerald-700" : "text-slate-700"}`}
                        data-testid={`inventory-history-seal-${tile.key}`}
                      >
                        {tile.value.toLocaleString()}
                      </div>
                    </div>
                  ))}
                  <div>
                    <div className="text-slate-400 font-semibold uppercase">{t.inventoryStoreLocation}</div>
                    <div className="text-slate-700 font-medium mt-0.5">{currentState.location || t.notAvailable}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 font-semibold uppercase">{t.inventoryGroup}</div>
                    <div className="text-slate-700 font-medium mt-0.5">
                      {currentState.groupAssignment || t.notAvailable}
                    </div>
                  </div>
                  {meta.meterType && (
                    <div>
                      <div className="text-slate-400 font-semibold uppercase">{t.inventoryMeterType}</div>
                      <div className="text-slate-700 font-medium mt-0.5">{meta.meterType}</div>
                    </div>
                  )}
                  {meta.meterMake && (
                    <div>
                      <div className="text-slate-400 font-semibold uppercase">{t.inventoryMeterMake}</div>
                      <div className="text-slate-700 font-medium mt-0.5">{meta.meterMake}</div>
                    </div>
                  )}
                  {meta.manufacturingYear && (
                    <div>
                      <div className="text-slate-400 font-semibold uppercase">{t.inventoryManufacturingYear}</div>
                      <div className="text-slate-700 font-medium mt-0.5">{meta.manufacturingYear}</div>
                    </div>
                  )}
                  {meta.simNo && (
                    <div>
                      <div className="text-slate-400 font-semibold uppercase">{t.inventorySimNo}</div>
                      <div className="text-slate-700 font-medium mt-0.5">{meta.simNo}</div>
                    </div>
                  )}
                </div>
                {fittedSeals.length > 0 && (
                  <SealNumbersPanel
                    label={t.inventorySealsFitted}
                    seals={fittedSeals}
                    infoTitle={t.inventorySealNumbersInfoTitle}
                    infoDescription={t.inventorySealNumbersInfo}
                    emptyText={t.inventorySealNoneFitted}
                    testId="inventory-history-seals-fitted"
                  />
                )}
              </div>

              {isSeal && (
                <div className="rounded-xl border border-slate-200 bg-white" data-testid="inventory-history-fitted-seals">
                  <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-100 text-xs text-slate-400 font-semibold uppercase">
                    <span>
                      {t.inventorySealsFitted} ({usedSeals.length})
                    </span>
                    <SealInfoIcon title={t.inventorySealRangeFittedInfoTitle} description={t.inventorySealRangeFittedInfo} />
                  </div>
                  {usedSeals.length ? (
                    <div className="overflow-x-auto max-h-72 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-500 sticky top-0">
                          <tr>
                            {[
                              t.inventorySealColSeal,
                              t.inventorySealColMeter,
                              t.inventoryHistoryTagAccount,
                              t.inventoryHistoryTagTask,
                              t.inventorySealColFittedBy,
                              t.inventorySealColDate,
                            ].map((label) => (
                              <th key={label} className="px-3 py-2 font-semibold text-start whitespace-nowrap">
                                {label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-slate-700">
                          {usedSeals.map((seal, index) => (
                            <tr key={`${seal.sealNumber}-${index}`}>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {seal.sealNumber ? <SealChips seals={[seal.sealNumber]} size="sm" /> : t.notAvailable}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {seal.meterInventoryItemId ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setMeterFromSeal({
                                        id: seal.meterInventoryItemId,
                                        meterNumber: seal.meterIdentifier,
                                      })
                                    }
                                    className="font-semibold text-[#236DB4] hover:underline focus:outline-none focus:ring-2 focus:ring-[#236DB4]/30 rounded"
                                    title={t.inventorySealOpenMeterHistory}
                                    data-testid="inventory-history-open-meter"
                                  >
                                    {seal.meterIdentifier || seal.meterInventoryItemId}
                                  </button>
                                ) : (
                                  seal.meterIdentifier || t.notAvailable
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">{seal.accountNo || t.notAvailable}</td>
                              <td className="px-3 py-2 whitespace-nowrap">{seal.taskId ?? t.notAvailable}</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {seal.usedByName ||
                                  (seal.usedBy != null
                                    ? t.inventoryHistoryUnknownUser.replace("{id}", seal.usedBy)
                                    : t.notAvailable)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-slate-500">
                                {formatEventTimestamp(seal.usedAt, langMode) || t.notAvailable}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="px-4 py-4 text-xs text-slate-500">{t.inventorySealNoneFitted}</div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2">
                <div className="flex bg-slate-100 rounded-lg p-1">
                  {viewFilters.map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      onClick={() => setViewFilter(filter)}
                      className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                        viewFilter === filter ? "bg-[#236DB4] text-white" : "text-slate-500"
                      }`}
                    >
                      {t[VIEW_FILTER_LABEL_KEYS[filter]]}
                    </button>
                  ))}
                </div>
                <span className="ms-auto text-xs text-slate-400 font-medium">
                  {t.inventoryHistoryEventsCount.replace("{count}", filteredEvents.length)}
                </span>
              </div>

              {loadError ? (
                <div className="text-sm text-slate-500 text-center py-6">{t.inventoryHistoryLoadFailed}</div>
              ) : !filteredEvents.length ? (
                <div className="text-sm text-slate-500 text-center py-6">{t.inventoryHistoryNoEventsForFilter}</div>
              ) : (
                <div>
                  {filteredEvents.map((event, index) => {
                    const Icon =
                      event.eventType === "SEAL_USED"
                        ? Stamp
                        : EVENT_ICON[event.transactionType || event.action] || Circle;
                    const tone = eventTone(event);
                    const title = eventTitle(event, t);
                    const tags = eventTagData(event);
                    const diffRows = buildFieldDiff(event);
                    const performerName =
                      tags.performerName || (tags.performerId != null ? userNames[tags.performerId] : null);
                    const isLast = index === filteredEvents.length - 1;
                    return (
                      <div key={`${event.id}-${event.eventType}-${index}`} className="flex gap-3">
                        <div className="flex flex-col items-center w-9 shrink-0">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${TONE_CLASS[tone]}`}>
                            <Icon size={15} />
                          </div>
                          {!isLast && <div className="w-px flex-1 bg-slate-200 my-1" />}
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="rounded-xl border border-slate-200 bg-white p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="font-semibold text-sm text-slate-800">{title}</div>
                                <div className="text-[11px] text-slate-400 mt-0.5">{event.eventType}</div>
                              </div>
                              <div className="text-[11px] text-slate-400 whitespace-nowrap">
                                {formatEventTimestamp(event.timestamp, langMode)}
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {tags.statusTransition && (
                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${TONE_CLASS.blue}`}>
                                  {tags.statusTransition.from} → {tags.statusTransition.to}
                                </span>
                              )}
                              {tags.locationMove && (
                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${TONE_CLASS.blue}`}>
                                  {tags.locationMove.from
                                    ? `${tags.locationMove.from} → ${tags.locationMove.to}`
                                    : `→ ${tags.locationMove.to}`}
                                </span>
                              )}
                              {tags.meterIdentifier && (
                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${TONE_CLASS.blue}`}>
                                  {t.inventorySealColMeter}: {tags.meterIdentifier}
                                </span>
                              )}
                              {tags.accountId && (
                                <span
                                  className={`px-2 py-0.5 rounded text-[10px] font-semibold ${TONE_CLASS.purple}`}
                                >
                                  {t.inventoryHistoryTagAccount}: {tags.accountId}
                                </span>
                              )}
                              {tags.taskId && (
                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${TONE_CLASS.teal}`}>
                                  {t.inventoryHistoryTagTask}: {tags.taskId}
                                </span>
                              )}
                              {tags.replacementId && (
                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${TONE_CLASS.green}`}>
                                  {t.inventoryHistoryTagReplacement}: {tags.replacementId}
                                </span>
                              )}
                              {tags.reference && (
                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${TONE_CLASS.slate}`}>
                                  {t.inventoryHistoryTagReference}: {tags.reference.type} #{tags.reference.id}
                                </span>
                              )}
                              {tags.itemType && (
                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${TONE_CLASS.slate}`}>
                                  {itemTypeOptionsFor(t).find((option) => option.value === tags.itemType)?.label ||
                                    tags.itemType}
                                </span>
                              )}
                              {tags.sealNumbers && (
                                <span className="inline-flex flex-wrap items-center gap-1 text-[10px] font-semibold text-emerald-700">
                                  {t.inventorySealsFitted}:
                                  <SealChips seals={tags.sealNumbers} size="sm" />
                                </span>
                              )}
                              {tags.note && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-semibold text-amber-700 bg-amber-50">
                                  {t.inventoryNote}: {tags.note}
                                </span>
                              )}
                              {tags.performerId != null && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-50 text-slate-600 border border-slate-200">
                                  {t.inventoryBy}:{" "}
                                  {performerName || t.inventoryHistoryUnknownUser.replace("{id}", tags.performerId)}
                                </span>
                              )}
                              {tags.source && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500">
                                  {tags.source}
                                </span>
                              )}
                            </div>

                            {diffRows.length > 0 && (
                              <div className="mt-2.5 pt-2.5 border-t border-slate-100 space-y-1.5">
                                {diffRows.map((row, rowIndex) => (
                                  <div key={`${row.field}-${rowIndex}`} className="flex items-center gap-2 text-xs">
                                    <span className="font-semibold text-slate-500 min-w-[90px]">
                                      {t[DIFF_FIELD_LABEL_KEYS[row.field]] || row.field}
                                    </span>
                                    <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600">
                                      {String(row.oldValue)}
                                    </span>
                                    <span className="text-slate-300">→</span>
                                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                                      {String(row.newValue)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

export default InventoryHistoryDrawer;
